import { Button } from '@chiroup/components/Button';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { CheckCircle as CheckCircleIcon, XCircle as XCircleIcon } from 'lucide-react';
import qs from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Language } from '../common/verbiage';
import userService from '../services/user.service';
import LoginContainer from './common/LoginContainer';

// Public, no-auth email-subscription page. Mounted at both:
//   - `/survey/unsubscribe` (review-link + 30-day-survey SES templates)
//   - `/email-subscription` (appointment-reminder template in
//     `createNotificationEmail.ts` and campaign email footers)
// Both URL shapes carry the same `?token=…&lang=es?` payload and round-trip
// to the `/subscription-status` endpoints. Preferences are category-scoped:
// "campaign" toggles `Patient.optOutCampaignEmail` (campaign email only) and
// "care" toggles `Patient.optOutEmail` (survey and reminder email).

const verbiage = {
  title: {
    en: 'Email preferences',
    es: 'Preferencias de correo electrónico',
  },
  subscribed: {
    en: 'You are currently subscribed to survey and reminder emails.',
    es: 'Actualmente está suscrito a los correos electrónicos de encuestas y recordatorios.',
  },
  unsubscribed: {
    en: 'You have been unsubscribed from survey and reminder emails.',
    es: 'Se ha dado de baja de los correos electrónicos de encuestas y recordatorios.',
  },
  campaignLabel: {
    en: 'Campaign emails (news and promotions)',
    es: 'Correos electrónicos de campañas (noticias y promociones)',
  },
  careLabel: {
    en: 'Survey and reminder emails',
    es: 'Correos electrónicos de encuestas y recordatorios',
  },
  statusSubscribed: {
    en: 'Subscribed',
    es: 'Suscrito',
  },
  statusUnsubscribed: {
    en: 'Unsubscribed',
    es: 'Dado de baja',
  },
  unsubscribeAction: {
    en: 'Unsubscribe',
    es: 'Cancelar suscripción',
  },
  resubscribeAction: {
    en: 'Resubscribe',
    es: 'Resuscribirse',
  },
  missingToken: {
    en: 'This unsubscribe link is invalid or has expired. Please use the link from your email.',
    es: 'Este enlace para cancelar la suscripción no es válido o ha caducado. Utilice el enlace de su correo electrónico.',
  },
  errorStatus: {
    en: 'Unable to load your subscription status. Please try again.',
    es: 'No se pudo cargar el estado de su suscripción. Por favor, inténtelo de nuevo.',
  },
  errorUpdate: {
    en: 'Unable to update your subscription. Please try again.',
    es: 'No se pudo actualizar su suscripción. Por favor, inténtelo de nuevo.',
  },
} satisfies Record<string, Record<Language, string>>;

type Scope = 'campaign' | 'care';

const SurveyUnsubscribe: React.FC = () => {
  const { search } = useLocation();
  const { token, lang } = qs.parse(search);
  const language: Language = lang === 'es' ? 'es' : 'en';
  const tokenStr = typeof token === 'string' ? token : '';

  const [status, setStatus] = useState<'loading' | 'ready' | 'invalid' | 'error'>(
    tokenStr ? 'loading' : 'invalid'
  );
  const [careSubscribed, setCareSubscribed] = useState<boolean | null>(null);
  // null = backend predates category preferences; fall back to the single
  // legacy care toggle so deploy order can never brick this public page.
  const [campaignSubscribed, setCampaignSubscribed] = useState<boolean | null>(null);
  const [submittingScope, setSubmittingScope] = useState<Scope | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenStr) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await userService.subscriptionStatus(tokenStr);

        if (cancelled) return;

        setCareSubscribed(!!res?.subscribed);
        setCampaignSubscribed(
          typeof res?.campaignSubscribed === 'boolean' ? res.campaignSubscribed : null
        );
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;

        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenStr]);

  const toggle = async (scope: Scope) => {
    const current = scope === 'campaign' ? campaignSubscribed : careSubscribed;

    if (!tokenStr || current === null || submittingScope) return;

    setSubmittingScope(scope);
    setErrorMessage(null);

    const action = current ? 'unsubscribe' : 'subscribe';

    try {
      // Omit the scope in legacy mode so an older backend applies its
      // original single-flag behavior.
      await userService.updateSubscription(
        tokenStr,
        action,
        campaignSubscribed === null ? undefined : scope
      );

      if (scope === 'campaign') {
        setCampaignSubscribed(action === 'subscribe');
      } else {
        setCareSubscribed(action === 'subscribe');
      }
    } catch (err) {
      setErrorMessage(verbiage.errorUpdate[language]);
    } finally {
      setSubmittingScope(null);
    }
  };

  if (status === 'loading') {
    return <LoadingPage />;
  }

  const preferenceRow = (scope: Scope, label: string, subscribed: boolean) => (
    <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4 text-left">
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className={`mt-1 text-sm ${subscribed ? 'text-green-600' : 'text-gray-500'}`}>
          {subscribed
            ? verbiage.statusSubscribed[language]
            : verbiage.statusUnsubscribed[language]}
        </p>
      </div>
      <Button
        onClick={() => toggle(scope)}
        text={
          subscribed ? verbiage.unsubscribeAction[language] : verbiage.resubscribeAction[language]
        }
        loading={submittingScope === scope}
      />
    </div>
  );

  return (
    <LoginContainer width="sm:max-w-xl">
      <div className="safe-area text-center">
        <h2 className="text-2xl font-extrabold text-gray-900">{verbiage.title[language]}</h2>

        {status === 'invalid' && (
          <div className="mt-6 rounded-md bg-red-50 p-4 text-left">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
              <p className="ml-3 text-sm text-red-700">{verbiage.missingToken[language]}</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="mt-6 rounded-md bg-red-50 p-4 text-left">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
              <p className="ml-3 text-sm text-red-700">{verbiage.errorStatus[language]}</p>
            </div>
          </div>
        )}

        {status === 'ready' && campaignSubscribed !== null && careSubscribed !== null && (
          <>
            {preferenceRow('campaign', verbiage.campaignLabel[language], campaignSubscribed)}
            {preferenceRow('care', verbiage.careLabel[language], careSubscribed)}
          </>
        )}

        {status === 'ready' && campaignSubscribed === null && careSubscribed !== null && (
          <>
            <p className="mt-6 text-gray-600">
              {careSubscribed ? verbiage.subscribed[language] : verbiage.unsubscribed[language]}
            </p>

            {!careSubscribed && (
              <div className="mt-4 flex items-center justify-center text-green-600">
                <CheckCircleIcon className="h-6 w-6 mr-2" aria-hidden="true" />
              </div>
            )}

            <Button
              className="mt-8"
              onClick={() => toggle('care')}
              text={
                careSubscribed
                  ? verbiage.unsubscribeAction[language]
                  : verbiage.resubscribeAction[language]
              }
              loading={submittingScope === 'care'}
            />
          </>
        )}

        {status === 'ready' && errorMessage && (
          <div className="mt-4 rounded-md bg-red-50 p-4 text-left">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
              <p className="ml-3 text-sm text-red-700">{errorMessage}</p>
            </div>
          </div>
        )}
      </div>
    </LoginContainer>
  );
};

export default SurveyUnsubscribe;
